function assertPositiveNumber(value, name) {
    if (!Number.isFinite(value) || value <= 0) {
        throw new Error(`${name} must be a positive number`);
    }
}

function calcPMT(principal, periodicRate, totalPeriods) {
    const p = Number(principal);
    const r = Number(periodicRate);
    const n = Number(totalPeriods);

    assertPositiveNumber(p, 'principal');
    assertPositiveNumber(n, 'totalPeriods');
    if (!Number.isFinite(r)) {
        throw new Error('periodicRate must be a finite number');
    }

    if (r === 0) {
        return p / n;
    }

    const factor = Math.pow(1 + r, n);
    return (p * r * factor) / (factor - 1);
}

function calcApproxEffectiveRate(nominalAnnualRatePct, periodsPerYear = 12) {
    const nominal = Number(nominalAnnualRatePct);
    const n = Number(periodsPerYear);

    if (!Number.isFinite(nominal)) {
        throw new Error('nominalAnnualRatePct must be a finite number');
    }
    assertPositiveNumber(n, 'periodsPerYear');

    if (nominal === 0) {
        return 0;
    }

    const r = nominal / 100;
    return (Math.pow(1 + r / n, n) - 1) * 100;
}

function calcRemainingPrincipal(principal, periodicRate, totalPeriods, paymentsMade) {
    const p = Number(principal);
    const r = Number(periodicRate);
    const n = Number(totalPeriods);
    const k = Number(paymentsMade);

    assertPositiveNumber(p, 'principal');
    assertPositiveNumber(n, 'totalPeriods');
    if (!Number.isFinite(r)) {
        throw new Error('periodicRate must be a finite number');
    }
    if (!Number.isFinite(k) || k < 0) {
        throw new Error('paymentsMade must be a non-negative number');
    }

    if (k === 0) {
        return p;
    }

    if (r === 0) {
        return Math.max(0, p - (p / n) * k);
    }

    const payment = calcPMT(p, r, n);
    const factor = Math.pow(1 + r, k);
    const remaining = p * factor - payment * ((factor - 1) / r);
    return Math.max(0, remaining);
}

function calcMlade({
    loanAmount,
    applicantAge,
    currency = 'EUR',
    euriborTenor,
    grossRatePct = 3.5,
    clientRatePct = 1.5,
    subsidyMonths = 72,
    bankMarginPct,
    bankMargin,
    euriborRate,
    totalYears
}) {
    const principal = Number(loanAmount);
    const totalY = Number(totalYears);
    const subsidyMonthsFixed = Number(subsidyMonths);

    if (!Number.isFinite(totalY) || totalY < 7 || totalY > 40) {
        throw new RangeError('totalYears must be between 7 and 40');
    }
    if (!Number.isFinite(subsidyMonthsFixed) || subsidyMonthsFixed !== 72) {
        throw new RangeError('subsidyMonths must be 72');
    }

    assertPositiveNumber(principal, 'loanAmount');

    const grossRate = Number(grossRatePct);
    const clientRate = Number(clientRatePct);

    if (!Number.isFinite(grossRate) || grossRate < 0) {
        throw new Error('grossRatePct must be a non-negative number');
    }
    if (!Number.isFinite(clientRate) || clientRate < 0) {
        throw new Error('clientRatePct must be a non-negative number');
    }
    if (grossRate < clientRate) {
        throw new RangeError('grossRatePct must be greater than or equal to clientRatePct');
    }

    const margin = Number.isFinite(Number(bankMarginPct)) ? Number(bankMarginPct) : Number(bankMargin);
    const euribor = Number(euriborRate);
    if (!Number.isFinite(margin)) {
        throw new Error('bankMarginPct must be a finite number');
    }
    if (!Number.isFinite(euribor)) {
        throw new Error('euriborRate must be a finite number');
    }
    if (margin < 0) {
        throw new Error('bankMarginPct must be a non-negative number');
    }

    const postSubsidyRatePct = margin + euribor;
    if (postSubsidyRatePct < 0) {
        throw new RangeError('postSubsidyRatePct must be non-negative');
    }

    const stateSubsidyPp = grossRate - clientRate;

    const totalPeriods = totalY * 12;
    const subsidyPeriods = subsidyMonthsFixed;
    const remainingPeriods = totalPeriods - subsidyPeriods;
    if (remainingPeriods <= 0) {
        throw new RangeError('totalYears must allow post-subsidy repayment period');
    }

    const phaseOneRate = clientRate / 100 / 12;
    const phaseTwoRate = postSubsidyRatePct / 100 / 12;

    const pmt1 = calcPMT(principal, phaseOneRate, totalPeriods);
    const remaining = calcRemainingPrincipal(principal, phaseOneRate, totalPeriods, subsidyPeriods);
    const pmt2 = remainingPeriods > 0 ? calcPMT(remaining, phaseTwoRate, remainingPeriods) : 0;

    const phaseOnePrincipalPaid = Math.max(0, principal - remaining);
    const subsidyPhaseInterest = Math.max(0, pmt1 * subsidyPeriods - phaseOnePrincipalPaid);
    const marketPhaseInterest = remainingPeriods > 0 ? Math.max(0, pmt2 * remainingPeriods - remaining) : 0;
    const totalInterest = subsidyPhaseInterest + marketPhaseInterest;
    const totalPayment = pmt1 * subsidyPeriods + pmt2 * remainingPeriods;
    const borrowerTotalInterest = totalPayment - principal;
    const blendedNominalRatePct = ((clientRate * subsidyPeriods) + (postSubsidyRatePct * remainingPeriods)) / totalPeriods;
    const approxEffectiveRatePct = calcApproxEffectiveRate(blendedNominalRatePct);

    return {
        loanAmount: principal,
        applicantAge: Number.isFinite(Number(applicantAge)) ? Number(applicantAge) : null,
        currency,
        euriborTenor: euriborTenor || '6M',
        euriborRate: round2(euribor),
        bankMarginPct: round2(margin),
        subsidyPayment: round2(pmt1),
        marketPayment: round2(pmt2),
        phase1Payment: round2(pmt1),
        phase2Payment: round2(pmt2),
        grossRatePct: round2(grossRate),
        clientRatePct: round2(clientRate),
        clientPhase1RatePct: round2(clientRate),
        postSubsidyRatePct: round2(postSubsidyRatePct),
        stateSubsidyPp: round2(stateSubsidyPp),
        subsidyRateDiffPct: round2(stateSubsidyPp),
        subsidyMonths: subsidyPeriods,
        remainingPrincipalAfterSubsidy: round2(remaining),
        principalExceedsSubsidyCap: principal > 100000,
        approxEffectiveRatePct,
        totalInterest: round2(totalInterest),
        borrowerTotalInterest: round2(borrowerTotalInterest),
        totalPayment: round2(totalPayment),
        subsidyPhaseInterest: round2(subsidyPhaseInterest),
        marketPhaseInterest: round2(marketPhaseInterest)
    };
}

function calcMaxLoan(monthlyIncome, monthlyObligations, debtToIncomeRatio, periodicRate, totalPeriods) {
    const income = Number(monthlyIncome);
    const obligations = Number(monthlyObligations);
    const dti = Number(debtToIncomeRatio);
    const r = Number(periodicRate);
    const n = Number(totalPeriods);

    if (!Number.isFinite(income) || !Number.isFinite(obligations) || !Number.isFinite(dti) || !Number.isFinite(r)) {
        throw new Error('inputs must be finite numbers');
    }
    assertPositiveNumber(n, 'totalPeriods');

    const maxPayment = income * dti;
    const availablePayment = maxPayment - obligations;
    if (availablePayment <= 0) {
        return 0;
    }

    if (r === 0) {
        return availablePayment * n;
    }

    const factor = Math.pow(1 + r, n);
    return availablePayment * ((factor - 1) / (r * factor));
}

function calcInterkalarna(loanAmount, annualRatePct, days) {
    const amount = Number(loanAmount);
    const rate = Number(annualRatePct);
    const durationDays = Number(days);

    if (!Number.isFinite(amount) || !Number.isFinite(rate) || !Number.isFinite(durationDays)) {
        throw new Error('inputs must be finite numbers');
    }

    if (rate < 0) {
        throw new Error('annualRatePct must be a non-negative number');
    }

    return amount * (rate / 100) * (durationDays / 365);
}

function clampRatePct(value) {
    return Math.min(30, Math.max(0, value));
}

function round2(value) {
    return Math.round(value * 100) / 100;
}

function calcVariable({
    loanAmount,
    introYears,
    introRate,
    euriborRate,
    bankMargin,
    totalYears,
    paymentsPerYear = 12
}) {
    const principal = Number(loanAmount);
    const introY = Number(introYears);
    const totalY = Number(totalYears);
    const ppy = Number(paymentsPerYear);

    if (!Number.isFinite(introY) || introY < 0) {
        throw new Error('introYears must be a non-negative number');
    }

    assertPositiveNumber(principal, 'loanAmount');
    assertPositiveNumber(totalY, 'totalYears');
    assertPositiveNumber(ppy, 'paymentsPerYear');

    if (!Number.isFinite(introRate) || Number(introRate) < 0) {
        throw new Error('introRate must be a non-negative number');
    }
    if (!Number.isFinite(euriborRate)) {
        throw new Error('euriborRate must be a finite number');
    }
    if (!Number.isFinite(bankMargin)) {
        throw new Error('bankMargin must be a finite number');
    }
    if (Number(bankMargin) < 0) {
        throw new Error('bankMargin must be a non-negative number');
    }

    const introRatePct = clampRatePct(Number(introRate));
    const euriborPct = clampRatePct(Number(euriborRate));
    const marginPct = clampRatePct(Number(bankMargin));
    const baseVariableRatePct = clampRatePct(euriborPct + marginPct);

    const totalPeriods = Math.round(totalY * ppy);
    const introPeriodsRaw = Math.round(introY * ppy);
    const introPeriods = Math.min(Math.max(introPeriodsRaw, 0), totalPeriods);
    const remainingPeriods = Math.max(0, totalPeriods - introPeriods);

    const introPeriodicRate = introRatePct / 100 / ppy;
    const variablePeriodicRate = baseVariableRatePct / 100 / ppy;

    const buildScenarios = ({
        remainingPrincipal,
        introPayment,
        pureSingleRate = false
    }) => {
        return [0, 1, 2].map((delta) => {
            const scenarioRate = clampRatePct(euriborPct + delta + marginPct);
            const scenarioPeriodicRate = scenarioRate / 100 / ppy;
            const scenarioPayment = pureSingleRate
                ? calcPMT(principal, scenarioPeriodicRate, totalPeriods)
                : (remainingPeriods > 0
                    ? calcPMT(remainingPrincipal, scenarioPeriodicRate, remainingPeriods)
                    : introPayment);
            const scenarioTotalPayment = pureSingleRate
                ? scenarioPayment * totalPeriods
                : (introPayment * introPeriods + scenarioPayment * remainingPeriods);
            const totalInterest = scenarioTotalPayment - principal;

            return {
                euriborDelta: delta,
                rate: round2(scenarioRate),
                payment: round2(scenarioPayment),
                totalInterest: round2(totalInterest),
                scenarioLabel: delta === 0 ? 'Trenutni EURIBOR' : `+${delta}% EURIBOR`
            };
        });
    };

    if (introPeriods === 0) {
        const singlePayment = calcPMT(principal, variablePeriodicRate, totalPeriods);
        const totalPayment = singlePayment * totalPeriods;
        const totalInterest = totalPayment - principal;
        const approxVariableEffectiveRatePct = round2(calcApproxEffectiveRate(baseVariableRatePct, ppy));

        return {
            loanAmount: principal,
            introYears: introY,
            introRate: introRatePct,
            euriborRate: euriborPct,
            bankMargin: marginPct,
            totalYears: totalY,
            paymentsPerYear: ppy,
            introPayment: round2(singlePayment),
            variablePayment: round2(singlePayment),
            totalPayment: round2(totalPayment),
            totalInterest: round2(totalInterest),
            approxEffectiveRatePct: approxVariableEffectiveRatePct,
            approxBlendedRatePct: approxVariableEffectiveRatePct,
            scenarios: buildScenarios({
                remainingPrincipal: principal,
                introPayment: singlePayment,
                pureSingleRate: true
            })
        };
    }

    if (introPeriods >= totalPeriods) {
        const fixedPayment = calcPMT(principal, introPeriodicRate, totalPeriods);
        const totalPayment = fixedPayment * totalPeriods;
        const totalInterest = totalPayment - principal;
        const roundedFixedPayment = round2(fixedPayment);
        const roundedTotalInterest = round2(totalInterest);
        const approxFixedEffectiveRatePct = round2(calcApproxEffectiveRate(introRatePct, ppy));

        return {
            loanAmount: principal,
            introYears: introY,
            introRate: introRatePct,
            euriborRate: euriborPct,
            bankMargin: marginPct,
            totalYears: totalY,
            paymentsPerYear: ppy,
            introPayment: roundedFixedPayment,
            variablePayment: roundedFixedPayment,
            totalPayment: round2(totalPayment),
            totalInterest: roundedTotalInterest,
            approxEffectiveRatePct: approxFixedEffectiveRatePct,
            approxBlendedRatePct: approxFixedEffectiveRatePct,
            scenarios: [{
                euriborDelta: 0,
                rate: round2(introRatePct),
                payment: roundedFixedPayment,
                totalInterest: roundedTotalInterest,
                scenarioLabel: 'Fiksna kamata'
            }]
        };
    }

    const introPayment = calcPMT(principal, introPeriodicRate, totalPeriods);
    const remainingPrincipal = calcRemainingPrincipal(principal, introPeriodicRate, totalPeriods, introPeriods);
    const variablePayment = calcPMT(remainingPrincipal, variablePeriodicRate, remainingPeriods);

    const totalPayment = introPayment * introPeriods + variablePayment * remainingPeriods;
    const totalInterest = totalPayment - principal;
    const weightedRatePct = ((introRatePct * introPeriods) + (baseVariableRatePct * remainingPeriods)) / totalPeriods;

    return {
        loanAmount: principal,
        introYears: introY,
        introRate: introRatePct,
        euriborRate: euriborPct,
        bankMargin: marginPct,
        totalYears: totalY,
        paymentsPerYear: ppy,
        introPayment: round2(introPayment),
        variablePayment: round2(variablePayment),
        totalPayment: round2(totalPayment),
        totalInterest: round2(totalInterest),
        approxEffectiveRatePct: round2(calcApproxEffectiveRate(baseVariableRatePct, ppy)),
        approxBlendedRatePct: round2(calcApproxEffectiveRate(weightedRatePct, ppy)),
        scenarios: buildScenarios({
            remainingPrincipal,
            introPayment,
            pureSingleRate: false
        })
    };
}

export {
    calcApproxEffectiveRate,
    calcInterkalarna,
    calcMaxLoan,
    calcMlade,
    calcPMT,
    calcVariable
};
